import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import * as elementsApi from '../api/elementsApi';
import type {
  OwnerKind,
  ConstellationView,
  CreateElementInput,
  CreateRelationInput,
  ElementDetail,
  ElementOwners,
  ElementWorkCounts,
  ElementType,
  ElementWorkRollup,
  RootElement,
  SetOwnersInput,
  UpdateElementInput,
} from '../model/types';

/**
 * ⚠ Klucze wsadowe (`workCounts`, `owners`, `rollup`) zawierają **całą listę
 * identyfikatorów**, więc każde rozwinięcie węzła tworzy NOWY klucz. Bez
 * `placeholderData: keepPreviousData` przy każdym kliknięciu w trójkąt `data`
 * spadało na `undefined` i **plakietki właścicieli znikały w całym drzewku**,
 * nie tylko w rozwijanej gałęzi — zgłoszone z obserwacji na żywym ekranie.
 *
 * Gorsza połowa tego samego objawu była niewidoczna: `workCounts` zasila `heat`,
 * czyli **wybór widocznej siódemki** (decyzja 45). Znikające ciepło na moment
 * przestawiało, które pozycje przechodzą przez próg — wiersze podskakiwały.
 *
 * Poprzednie dane są tu bezpieczne, bo obie odpowiedzi to **mapy po identyfikatorze**:
 * wpis dla elementu, o który już pytaliśmy, nie przestaje być prawdziwy, gdy do zbioru
 * dojdą kolejne. Nowe wiersze pojawiają się bez plakietki i dostają ją po odpowiedzi —
 * treść dochodzi, zamiast istniejąca znikać.
 */
export const elementKeys = {
  all: ['elements'] as const,
  detail: (id: number) => ['elements', 'detail', id] as const,
  constellation: (id: number, depth?: number) =>
    ['elements', 'constellation', id, depth ?? 'default'] as const,
  workCounts: (ids: readonly number[]) => ['elements', 'work-counts', [...ids].sort()] as const,
  owners: (ids: readonly number[]) => ['elements', 'owners', [...ids].sort()] as const,
  rollup: (ids: readonly number[], depth?: number) =>
    // ⚠ Zasięg JEST częścią klucza: `Σ` o innym zasięgu to inna odpowiedź na to samo
    //   pytanie, więc wspólny klucz podałby liczbę z poprzedniego trybu jako bieżącą.
    ['elements', 'rollup', [...ids].sort(), depth ?? 'branch'] as const,
  roots: () => ['elements', 'roots'] as const,
  types: (activeOnly: boolean) => ['elements', 'types', activeOnly] as const,
  history: (id: number, humanOnly: boolean) => ['elements', 'history', id, humanOnly] as const,
  historyRun: (id: number, runId: number) => ['elements', 'history', id, 'run', runId] as const,
};

/**
 * Słownik typów elementu jako **opcje zapytania**, a nie hook (decyzja 83).
 *
 * ⭐ Ten sam powód co przy `orgQueryOptions` w `identity`: omnibox rozwiązuje kilka nazw naraz
 * własnym `useQueries`, więc hook zmusiłby go do wołania w pętli albo kazał `elements` przejąć
 * orkiestrację cudzego ekranu. **Właściciel danych trzyma trasę i klucz, wołający — kolejność
 * i liczbę zapytań.**
 *
 * `activeOnly` jest w kluczu, bo to **dwa różne pytania**: formularz elementu chce oferty,
 * ustawienia chcą też wycofanych — inaczej nie dałoby się ich przywrócić.
 */
export function elementTypesQueryOptions(activeOnly: boolean) {
  return {
    queryKey: elementKeys.types(activeOnly),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      elementsApi.fetchElementTypes(activeOnly, signal),
    // Słownik zmienia się rzadko, a czyta go każdy formularz elementu i omnibox.
    staleTime: 60_000,
  };
}

export function useElementTypes(activeOnly: boolean) {
  return useQuery<ElementType[]>(elementTypesQueryOptions(activeOnly));
}

/**
 * Zmiany w słowniku. ⚠ **Zmiana nazwy przepisuje elementy** (serwer, jedna transakcja), więc
 * unieważnia nie tylko listę typów — inaczej ekran elementu pokazywałby starą nazwę do
 * najbliższego odświeżenia, a drzewko filtrowałoby po wartości, której już nie ma.
 */
function useElementTypeMutation<TInput, TResult>(
  run: (input: TInput) => Promise<TResult>,
  touchesElements: boolean,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: run,

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['elements', 'types'] });

      if (touchesElements) {
        void queryClient.invalidateQueries({ queryKey: ['elements', 'detail'] });
        void queryClient.invalidateQueries({ queryKey: ['elements', 'constellation'] });
      }
    },
  });
}

export function useCreateElementType() {
  return useElementTypeMutation((name: string) => elementsApi.createElementType(name), false);
}

export function useRenameElementType() {
  return useElementTypeMutation(
    (input: { id: number; name: string }) => elementsApi.renameElementType(input.id, input.name),
    true,
  );
}

export function useSetElementTypeActive() {
  return useElementTypeMutation(
    (input: { id: number; isActive: boolean }) =>
      elementsApi.setElementTypeActive(input.id, input.isActive),
    false,
  );
}

export function useDeleteElementType() {
  return useElementTypeMutation((id: number) => elementsApi.deleteElementType(id), false);
}

/**
 * Korzenie grafu pod ekran wejściowy `/constellation`.
 *
 * Bez parametrów, więc klucz jest stały — lista zmienia się tylko wtedy, gdy ktoś
 * doda albo przeniesie element najwyższego poziomu, czyli rzadko.
 */
export function useRootElements() {
  return useQuery<RootElement[]>({
    queryKey: elementKeys.roots(),
    queryFn: ({ signal }) => elementsApi.fetchRootElements(signal),
  });
}

export function useElement(id: number) {
  return useQuery<ElementDetail>({
    queryKey: elementKeys.detail(id),
    queryFn: ({ signal }) => elementsApi.fetchElement(id, signal),
  });
}

export function useConstellation(id: number) {
  return useQuery<ConstellationView>({
    queryKey: elementKeys.constellation(id),
    queryFn: ({ signal }) => elementsApi.fetchConstellation(id, signal),
  });
}

/**
 * Liczniki pracy dla widocznego rozwinięcia (decyzja 35).
 *
 * ⚠ Osobne zapytanie, wołane RÓWNOLEGLE z drzewkiem, a nie po nim. Zlanie ich
 * w jedno związałoby `Graph` z `Commitments` w warstwie HTTP, którą świadomie
 * trzymamy rozdzieloną portem `IElementUsage` — a przy okazji drzewko czekałoby
 * na wolniejsze z dwóch zapytań, zamiast malować się od razu.
 */
export function useWorkCounts(elementIds: readonly number[]) {
  return useQuery<ElementWorkCounts[], Error, Map<number, ElementWorkCounts>>({
    queryKey: elementKeys.workCounts(elementIds),
    queryFn: ({ signal }) => elementsApi.fetchWorkCounts(elementIds, signal),
    // Wiersz drzewka pyta o liczby po identyfikatorze, więc mapa jest tym,
    // czego naprawdę potrzebuje — a `select` liczy ją raz na odpowiedź,
    // nie przy każdym renderowaniu.
    select: (rows) => new Map(rows.map((row) => [row.elementId, row])),
    enabled: elementIds.length > 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * Efektywni właściciele dla widocznego rozwinięcia (spec 9.1: z proweniencją).
 *
 * Trzecie z trzech zapytań drzewka, wołane RÓWNOLEGLE z pozostałymi. Trzy żądania
 * kosztują tyle co jedno (HTTP/2 je multipleksuje), a jeden gruby endpoint zlepiłby
 * `Graph` z `Commitments` w warstwie HTTP.
 */
export function useElementOwners(elementIds: readonly number[]) {
  return useQuery<ElementOwners[], Error, Map<number, ElementOwners['owners']>>({
    queryKey: elementKeys.owners(elementIds),
    queryFn: ({ signal }) => elementsApi.fetchOwners(elementIds, signal),
    select: (rows) => new Map(rows.map((row) => [row.elementId, row.owners])),
    enabled: elementIds.length > 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * Agregat `Σ` — praca w **poddrzewie po `has`** (decyzja 75, domyka B0).
 *
 * ⚠ Zawsze po `has`, **także w soczewce zależności**. To rozstrzygnięcie usera:
 * B0 pytało, czy w soczewce liczyć zasięg po tej relacji — na teraz nie, bo
 * „ile roboty jest w tej gałęzi" ma znaczyć to samo niezależnie od tego, przez
 * którą soczewkę się patrzy. Zasięg po zależności to inna liczba i inne zapytanie.
 */
export function useWorkRollup(elementIds: readonly number[], depth?: number) {
  return useQuery<ElementWorkRollup[], Error, Map<number, ElementWorkRollup>>({
    queryKey: elementKeys.rollup(elementIds, depth),
    queryFn: ({ signal }) => elementsApi.fetchWorkRollup(elementIds, depth, signal),
    select: (rows) => new Map(rows.map((row) => [row.elementId, row])),
    enabled: elementIds.length > 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * Tworzenie elementu — używane przez omnibox (`+`, spec 6.4).
 *
 * ⚠ **Bez optimistic UI**, w odróżnieniu od edycji: element dostaje identyfikator dopiero
 * z serwera, a zaraz po utworzeniu na niego **nawigujemy**. Wstawienie tymczasowego wiersza
 * dałoby ekran pod adresem, który przez chwilę nie istnieje — a to gorsze niż jedno
 * oczekiwanie na odpowiedź.
 */
export function useCreateElement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateElementInput) => elementsApi.createElement(input),

    onSuccess: () => {
      // Nowy element bez rodzica jest korzeniem, więc zmienia listę wejściową.
      void queryClient.invalidateQueries({ queryKey: elementKeys.roots() });
    },
  });
}

/**
 * Nowa krawędź — używane przez omnibox (`>`, spec 6.5) i przez `+` z `@in`.
 *
 * Unieważnia **całe** drzewko, nie jeden węzeł: krawędź zmienia widok z obu stron,
 * a przy `has` przesuwa element w strukturze, więc lista korzeni też może się zmienić.
 */
export function useCreateRelation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateRelationInput) => elementsApi.createRelation(input),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['elements', 'constellation'] });
      void queryClient.invalidateQueries({ queryKey: elementKeys.roots() });
      // ⚠⚠ Nowa krawędź `has` zmienia DWIE rzeczy poza samą strukturą, a obie widać
      // w tym samym wierszu: własność spływa w dół (spec 9.1), więc dziecko dostaje
      // właścicieli rodzica, i zmienia się przynależność do poddrzewa, czyli agregat `Σ`.
      // ⭐ Stała tu wcześniej pojedyncza linia unieważniająca `relation-counts` — klucz,
      // którego od dec. 166 nie czyta już nic (a i wcześniej nie czytał go żaden hook).
      // Czyli: jedno unieważnienie bez odbiorcy tuż obok dwóch brakujących.
      void queryClient.invalidateQueries({ queryKey: ['elements', 'owners'] });
      void queryClient.invalidateQueries({ queryKey: ['elements', 'rollup'] });
    },
  });
}

/**
 * Przypisanie właściciela — używane przez omnibox (`+ @owner:` i `> @owner`, spec 6.4–6.5).
 *
 * ⭐ Unieważnia **cały** worek właścicieli, nie wiersz tego elementu: własność
 * **dziedziczy w dół po `has`** (spec 9.1), więc jedno przypisanie zmienia plakietki
 * na całym poddrzewie. Wiersz-po-wierszu byłby tu cichym błędem — potomkowie pokazywaliby
 * poprzedniego właściciela do najbliższego odświeżenia.
 *
 * ⛔ Bez optimistic UI: efektywnych właścicieli liczy serwer razem z proweniencją
 * („odziedziczone z: …"), a tego front nie umie przewidzieć, bo nie zna kształtu poddrzewa.
 */
export function useSetOwners() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SetOwnersInput) => elementsApi.setOwners(input),

    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: ['elements', 'owners'] });
      void queryClient.invalidateQueries({ queryKey: elementKeys.detail(input.elementId) });
    },
  });
}

/**
 * Edycja elementu — szablon optimistic ze spec 4B.
 *
 * `onMutate` robi migawkę i pokazuje wynik natychmiast · `onError` wycofuje
 * i mówi, co się stało · `onSettled` unieważnia, żeby prawda przyszła
 * z serwera. Ten sam szablon obowiązuje każdą kolejną mutację w projekcie.
 */
export function useUpdateElement(id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateElementInput) => elementsApi.updateElement(id, input),

    onMutate: async (input) => {
      // Bez tego trwające zapytanie mogłoby dojechać PO naszej podmianie
      // i nadpisać ją starą odpowiedzią.
      await queryClient.cancelQueries({ queryKey: elementKeys.detail(id) });

      const snapshot = queryClient.getQueryData<ElementDetail>(elementKeys.detail(id));

      if (snapshot) {
        queryClient.setQueryData<ElementDetail>(elementKeys.detail(id), {
          ...snapshot,
          ...(input.title === undefined ? {} : { title: input.title }),
          ...(input.type === undefined ? {} : { type: input.type }),
          ...(input.description === undefined ? {} : { description: input.description }),
        });
      }

      return { snapshot };
    },

    onError: (_error, _input, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(elementKeys.detail(id), context.snapshot);
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: elementKeys.detail(id) });
      // Tytuł widać też w drzewku, a ono ma własny klucz — bez tego wiersz
      // zostaje ze starą nazwą do następnego przeładowania.
      void queryClient.invalidateQueries({ queryKey: ['elements', 'constellation'] });
    },
  });
}

/**
 * Historia elementu — stronicowana **kotwicą**, nie numerem strony (decyzja 104).
 *
 * ⭐ `useInfiniteQuery`, bo historia rośnie w dół i nigdy nie wraca się do „strony 3": jedyną
 * sensowną akcją jest „pokaż starsze". Numer strony byłby tu kłamstwem także dlatego, że między
 * żądaniami ktoś może dopisać ogniwo i przesunąć całą numerację.
 *
 * ⚠ Filtr „tylko ludzie" siedzi w KLUCZU, a nie w parametrze wywołania — inaczej przełączenie
 * dokładałoby strony przefiltrowane do już wczytanych nieprzefiltrowanych.
 */
export function useElementHistory(elementId: number, humanOnly: boolean, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: elementKeys.history(elementId, humanOnly),
    queryFn: ({ pageParam, signal }) =>
      elementsApi.fetchElementHistory(elementId, { before: pageParam, humanOnly, limit: 40 }, signal),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextBefore,
    enabled,
  });
}

/**
 * Rozwinięcie jednego przebiegu importu — ogniwo po ogniwie.
 *
 * ⚠ Osobne zapytanie, nie część strony: rozwinięcie dotyczy jednego zdarzenia z wielu,
 * a wciągnięcie go do strony unieważniałoby przy każdym kliknięciu całą wczytaną historię.
 */
export function useElementHistoryRun(elementId: number, runId: number | null) {
  return useQuery({
    queryKey: elementKeys.historyRun(elementId, runId ?? 0),
    queryFn: ({ signal }) => elementsApi.fetchElementHistory(elementId, { runId: runId! }, signal),
    enabled: runId !== null,
  });
}

/**
 * Zmiany własności na jednym elemencie — **optimistic**, bo każda z nich jest odwracalna.
 *
 * ⭐ Migawka obejmuje CAŁY detal elementu, nie samą listę właścicieli: `canManageOwners`
 * i proweniencja liczone są przez serwer razem z nią, więc cofnięcie musi przywrócić komplet.
 *
 * ⚠ Wykluczenie i cofnięcie wykluczenia **nie zmieniają listy lokalnych właścicieli**, tylko
 * to, czy odziedziczony wchodzi do efektywnych — dlatego migawkę patchujemy przez usunięcie
 * albo przez unieważnienie, a nie przez zgadywanie, kogo element odziedziczy.
 */
export function useOwnerActions(elementId: number) {
  const queryClient = useQueryClient();
  const key = elementKeys.detail(elementId);

  async function snapshot() {
    await queryClient.cancelQueries({ queryKey: key });
    return queryClient.getQueryData<ElementDetail>(key);
  }

  function drop(kind: OwnerKind, ownerId: number) {
    queryClient.setQueryData<ElementDetail>(key, (detail) =>
      detail === undefined
        ? detail
        : {
            ...detail,
            owners: detail.owners.filter(
              (owner) => !(owner.ownerKind === kind && owner.ownerId === ownerId),
            ),
          },
    );
  }

  function settle(previous: ElementDetail | undefined) {
    if (previous) queryClient.setQueryData(key, previous);
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: key });
    // ⚠ Własność dziedziczy się w dół, więc zmiana tutaj rusza plakietki w CAŁYM drzewku —
    // nie tylko na tym elemencie.
    void queryClient.invalidateQueries({ queryKey: ['elements', 'owners'] });
    void queryClient.invalidateQueries({ queryKey: ['elements', 'constellation'] });
  }

  const remove = useMutation({
    mutationFn: ({ kind, ownerId }: { kind: OwnerKind; ownerId: number }) =>
      elementsApi.removeOwner(elementId, kind, ownerId),
    onMutate: async ({ kind, ownerId }) => {
      const previous = await snapshot();
      drop(kind, ownerId);
      return { previous };
    },
    onError: (_error, _input, context) => settle(context?.previous),
    onSettled: invalidate,
  });

  const exclude = useMutation({
    mutationFn: ({ kind, ownerId }: { kind: OwnerKind; ownerId: number }) =>
      elementsApi.excludeOwner(elementId, kind, ownerId),
    onMutate: async ({ kind, ownerId }) => {
      const previous = await snapshot();
      drop(kind, ownerId);
      return { previous };
    },
    onError: (_error, _input, context) => settle(context?.previous),
    onSettled: invalidate,
  });

  const restore = useMutation({
    mutationFn: ({ kind, ownerId }: { kind: OwnerKind; ownerId: number }) =>
      elementsApi.restoreOwner(elementId, kind, ownerId),
    // ⛔ Bez optimistic: cofnięcie wykluczenia PRZYWRACA właściciela, którego kształtu
    // (proweniencja, źródło) front nie zna — zgadywanie dałoby wiersz z pustą nazwą.
    onSettled: invalidate,
  });

  return { remove, exclude, restore };
}
